/* eslint-disable no-restricted-syntax */
import {
  BUSINESS_PACKAGE_CODES,
  DURATION,
  FREE_PACKAGE_CODES,
  LOW_CREDITS,
  MERGE_PRODUCT_TYPE,
  MILLISECONDS_IN_DAY,
  PACK_DURATION,
  PACKAGE_CHANGE_TYPES,
  PACKAGE_CODE,
  PACKAGE_DURATION,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_ORDER,
  PACKAGE_LEVEL_V3,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
  PRODUCT_TYPE,
  RESOURCES_TYPE,
  WORD_PER_PAGE,
  WORDS_PER_MINUTE,
} from '@src/constants/package';
import { formatNumber, roundNumber } from '@src/utils/number';
import { checkVietNam } from '@src/utils/checkCountry';
import { useTranslation } from 'react-i18next';
import { LANGUAGE } from '@src/constants';
import moment from 'moment';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import { VBEE_CONSOLE_URL } from '@src/configs';
import route from '@src/constants/route';
import { capitalize } from 'lodash';
import { getPriceFromVoucher } from './voucher';

const getPackageChangeType = ({
  currPackage,
  prevPackage,
  prevPackageExpiredDate,
}) => {
  if (!prevPackageExpiredDate) return null; // FREE package
  if (!prevPackage || !prevPackage?.cycle) return null; // is not cycle package
  const isPrevPackageExpired = moment().isAfter(prevPackageExpiredDate);
  if (isPrevPackageExpired) return null;

  const MAPPING_PACKAGE_LEVEL = {
    [PACKAGE_LEVEL.ADVANCE]: 0,
    [PACKAGE_LEVEL.PRO]: 1,
    [PACKAGE_LEVEL.PROFESSIONAL]: 2,
    [PACKAGE_LEVEL.ENTERPRISE]: 3,
    [PACKAGE_LEVEL.BASIC]: 0,
    [PACKAGE_LEVEL.STANDARD]: 1,
    [PACKAGE_LEVEL.SPECIAL]: 2,
    [PACKAGE_LEVEL.VIP]: 3,
    [PACKAGE_LEVEL_V3.CUSTOM]: 4,
  };

  const isDowngrade =
    MAPPING_PACKAGE_LEVEL[currPackage.level] <
    MAPPING_PACKAGE_LEVEL[prevPackage.level];

  if (currPackage?.level === prevPackage?.level) {
    if (currPackage?.expiresIn > prevPackage?.expiresIn)
      return PACKAGE_CHANGE_TYPES.UPGRADE;
    if (currPackage?.expiresIn < prevPackage?.expiresIn)
      return PACKAGE_CHANGE_TYPES.DOWNGRADE;

    return null;
  }

  if (isDowngrade) return PACKAGE_CHANGE_TYPES.DOWNGRADE;
  return PACKAGE_CHANGE_TYPES.UPGRADE;
};

const filterPackages = ({ duration, data, packageType, isDubbingPackages }) => {
  const packageLevels = [
    PACKAGE_LEVEL.DUBBING_BASIC,
    PACKAGE_LEVEL.BASIC,
    PACKAGE_LEVEL.ADVANCE,
    PACKAGE_LEVEL.PRO,
    PACKAGE_LEVEL.ENTERPRISE,
    PACKAGE_LEVEL.PROFESSIONAL,
    PACKAGE_LEVEL.STANDARD,
    PACKAGE_LEVEL.BUSINESS,
  ];

  let packages = [];

  switch (duration) {
    case DURATION.MONTHLY: {
      packages = data.filter(
        (pkg) =>
          pkg.active &&
          (pkg.expiresIn < 0 ||
            pkg.expiresIn === 30 ||
            BUSINESS_PACKAGE_CODES.includes(pkg.code)) &&
          packageLevels.includes(pkg.level),
      );
      break;
    }
    case DURATION.QUARTERLY: {
      packages = data.filter(
        (pkg) =>
          pkg.active &&
          (pkg.expiresIn < 0 || pkg.expiresIn === 90) &&
          packageLevels.includes(pkg.level),
      );
      break;
    }
    case DURATION.YEARLY: {
      packages = data.filter(
        (pkg) =>
          pkg.active &&
          (pkg.expiresIn < 0 || pkg.expiresIn === 365) &&
          packageLevels.includes(pkg.level),
      );
      break;
    }
    default:
      break;
  }

  const shouldAddApiEnterprisePackage =
    packageType === PACKAGE_TYPE.API &&
    !packages.some((pkg) => pkg.code === PACKAGE_CODE.API_ENTERPRISE_YEAR_V2);

  if (shouldAddApiEnterprisePackage) {
    const apiEnterprisePackage = data.find(
      (pkg) => pkg.code === PACKAGE_CODE.API_ENTERPRISE_YEAR_V2,
    );
    if (apiEnterprisePackage) packages.push(apiEnterprisePackage);
  }

  const isVietNam = checkVietNam();
  if (!isVietNam)
    packages = packages.filter(
      (pkg) =>
        pkg.code !== PACKAGE_CODE.API_PAYG &&
        pkg.code !== PACKAGE_CODE.API_ENTERPRISE_YEAR_V2,
    );

  // Remove auto renew package
  packages = packages.filter(
    (pkg) => pkg.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW,
  );

  const dubbingTrialPackage = data.find(
    (pkg) => pkg.code === PACKAGE_CODE.DUBBING_TRIAL,
  );

  // push dubbing trial package to first packages
  if (isDubbingPackages && dubbingTrialPackage) {
    packages = packages.filter(
      (pkg) => pkg.code !== PACKAGE_CODE.DUBBING_TRIAL,
    );
    packages.unshift(dubbingTrialPackage);
  }

  return packages;
};

const filterPackagesV3 = ({ duration, allPackages, packageType }) => {
  let packages = [];

  switch (duration) {
    case DURATION.MONTHLY: {
      packages = allPackages.filter(
        (pkg) =>
          pkg.active &&
          (pkg.expiresIn === PACK_DURATION.INFINITE ||
            pkg.expiresIn === PACK_DURATION.MONTH),
      );
      break;
    }

    case DURATION.YEARLY: {
      const durationYear =
        packageType === PACKAGE_TYPE.API
          ? PACKAGE_EXPIRED.YEARLY
          : PACK_DURATION.YEAR;
      packages = allPackages.filter(
        (pkg) =>
          pkg.active &&
          (pkg.expiresIn === PACK_DURATION.INFINITE ||
            pkg.expiresIn === durationYear),
      );
      break;
    }
    default:
      break;
  }

  // Remove auto renew package
  packages = packages.filter(
    (pkg) => pkg.renewalType !== PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW,
  );

  packages = packages?.filter(
    (pkg) => !BUSINESS_PACKAGE_CODES.includes(pkg?.code),
  );

  return packages?.flatMap((item) => (!item?.isTopUp ? item : []));
};

const getPackageName = (
  name,
  code,
  language,
  translation,
  onlyShowName = true,
  isMirgatedToV3Pkg = false,
) => {
  try {
    let t = translation;
    if (!translation) t = useTranslation().t;

    if (!code) return '';
    let duration = '';
    let displayName = '';

    if (code.includes('MONTH')) duration = t('packageDurationMonth');
    if (code.includes('YEAR')) duration = t('packageDurationYear');
    if (code.includes('QUARTER'))
      duration = isMirgatedToV3Pkg ? t('packageDurationMonth') : t('quarterly');

    // Remove duration for package API v3
    if (
      code.includes(PACKAGE_TYPE.API) &&
      code?.includes(PACKAGE_STUDIO_VERSION.V3)
    )
      duration = '';

    // Remove duration for custom package
    if (code.includes('CUSTOM')) duration = '';

    if (
      name &&
      !code.includes(PACKAGE_LEVEL.TRIAL) &&
      !code.includes(PACKAGE_LEVEL.BASIC)
    ) {
      displayName = name[language] || name[LANGUAGE.EN];
    }
    if (code.includes(PACKAGE_LEVEL.TRIAL)) {
      displayName = onlyShowName ? t('trial') : t('packageLevelTrial');
    }
    if (code.includes(PACKAGE_LEVEL.BASIC)) {
      displayName = t(onlyShowName ? 'basic' : 'packageLevelBasic');
    }
    if (code.includes(PACKAGE_LEVEL.PRO)) {
      displayName = t(onlyShowName ? 'pro' : 'packageLevelPro');
    }
    // Hot fix for misspell package code API
    if (
      code.includes(PACKAGE_LEVEL.PROFESSIONAL) ||
      code?.includes('PROFESTIONAL')
    ) {
      displayName = t(
        onlyShowName ? 'professional' : 'packageLevelProfessional',
      );
    }
    // change package name for old studio package (V1/V2)
    if (
      code.includes(PACKAGE_LEVEL.PROFESSIONAL) &&
      isMirgatedToV3Pkg &&
      code?.includes(PACKAGE_TYPE.STUDIO)
    ) {
      displayName = t(onlyShowName ? 'enterprise' : 'packageLevelEnterPrise');
    }
    if (code.includes('ADV')) {
      displayName = t(onlyShowName ? 'advance' : 'packageLevelAdvance');
    }
    if (code.includes(PACKAGE_LEVEL.STANDARD)) {
      displayName = t(onlyShowName ? 'standard' : 'packageLevelStandard');
    }
    if (
      code.includes(PACKAGE_LEVEL.ENTERPRISE) ||
      code.includes(PACKAGE_LEVEL.SPECIAL)
    ) {
      displayName = t(onlyShowName ? 'enterprise' : 'packageLevelEnterPrise');
    }

    if (code.includes(PACKAGE_LEVEL.VIP))
      displayName = t(onlyShowName ? 'vip' : 'packageLevelVip');
    if (code.includes(PACKAGE_LEVEL.CUSTOM))
      displayName = t(onlyShowName ? 'vipPlus' : 'packageLevelVipPlus');

    return duration ? `${displayName} - ${duration}` : displayName;
  } catch (error) {
    return '';
  }
};

const getPackageNameV3 = (
  name,
  code,
  language,
  translation,
  onlyShowName = true,
  isMirgatedToV3Pkg = false,
) => {
  try {
    let t = translation;
    if (!translation) t = useTranslation().t;

    if (!code) return '';
    let duration = '';
    let displayName = '';

    if (code.includes('MONTH')) duration = capitalize(t('month'));
    if (code.includes('YEAR')) duration = capitalize(t('year'));
    if (code.includes('CUSTOM')) duration = '';

    if (
      name &&
      !code.includes(PACKAGE_LEVEL.TRIAL) &&
      !code.includes(PACKAGE_LEVEL.BASIC)
    )
      displayName = name[language] || name[LANGUAGE.EN];

    if (code.includes(PACKAGE_LEVEL.PRO)) {
      displayName = t(onlyShowName ? 'pro' : 'packageLevelPro');
    }
    if (code.includes(PACKAGE_LEVEL.PROFESSIONAL) && !isMirgatedToV3Pkg) {
      displayName = t(
        onlyShowName ? 'professional' : 'packageLevelProfessional',
      );
    }
    if (code.includes(PACKAGE_LEVEL.PROFESSIONAL) && isMirgatedToV3Pkg) {
      displayName = t(onlyShowName ? 'enterprise' : 'packageLevelEnterPrise');
    }
    if (code.includes('ADV')) {
      displayName = t(onlyShowName ? 'advance' : 'packageLevelAdvance');
    }
    if (code.includes(PACKAGE_LEVEL.STANDARD)) {
      displayName = t(onlyShowName ? 'standard' : 'packageLevelStandard');
    }
    if (
      code.includes(PACKAGE_LEVEL.ENTERPRISE) ||
      code.includes(PACKAGE_LEVEL.SPECIAL)
    ) {
      displayName = t(onlyShowName ? 'enterprise' : 'packageLevelEnterPrise');
    }

    return duration ? `${displayName} - ${duration}` : displayName;
  } catch (error) {
    return '';
  }
};

const getPackagesName = (packages, language, translation) => {
  if (!packages?.length) return '';
  const names = packages.map(({ name, code }) =>
    getPackageName(name, code, language, translation),
  );

  // group names by display name, format: "displayName - duration1/duration2"
  const groupByDisplayName = names.reduce((acc, name) => {
    const [displayName, duration] = name.split(' - ');
    if (!acc[displayName]) acc[displayName] = [];
    acc[displayName].push(duration);
    return acc;
  }, {});

  // sort duration month - quarterly - year
  const sortDuration = (a, b) => {
    switch (a) {
      case translation('packageDurationMonth'):
        return -1;
      case translation('quarterly'): {
        if (b === translation('packageDurationMonth')) return 1;
        return -1;
      }
      case translation('packageDurationYear'):
        return 1;
      default:
        return 0;
    }
  };

  return Object.entries(groupByDisplayName)
    .map(([displayName, durations]) => {
      const duration = durations.sort(sortDuration).join('/');
      return duration ? `${displayName} - ${duration}` : displayName;
    })
    .join(', ');
};

const checkPackageDiscount = (discountConfig = {}) => {
  const { startAt, endAt } = discountConfig || {};
  const inDiscountTime = startAt && moment().isBetween(startAt, endAt);

  return inDiscountTime;
};

const checkHasDiscountPackage = (listPackages) => {
  let hasDiscountPackage = false;
  for (const currentPackage of listPackages) {
    const inDiscountTime = checkPackageDiscount(currentPackage?.discountConfig);
    if (inDiscountTime) {
      hasDiscountPackage = true;
      break;
    }
  }

  return hasDiscountPackage;
};

const getNewPackageCurrentPrice = (pkg = {}) => {
  const { price, usdPrice, monthlyPrice, usdMonthlyPrice, discountConfig } =
    pkg;
  const isInDiscount = checkPackageDiscount(discountConfig);

  if (isInDiscount)
    return {
      price: discountConfig.price,
      usdPrice: discountConfig.usdPrice,
      monthlyPrice: discountConfig.monthlyPrice,
      usdMonthlyPrice: discountConfig.usdMonthlyPrice,
    };

  return { price, usdPrice, monthlyPrice, usdMonthlyPrice };
};

const getOldPackageCurrentPrice = (pkg = {}) => {
  const { price, usdPrice, monthlyPrice, usdMonthlyPrice } = pkg;
  return { price, usdPrice, monthlyPrice, usdMonthlyPrice };
};

const getPackageCurrentPrice = (pkg, enableNewPackage) => {
  if (enableNewPackage) return getNewPackageCurrentPrice(pkg);
  return getOldPackageCurrentPrice(pkg);
};

const getDiscountPackage = (packages) =>
  packages.find((pkg) => checkPackageDiscount(pkg?.discountConfig));

const getMostPopularPackage = (packages) =>
  packages.find((pkg) => pkg.mostPopular);

const checkAutoRenewalPackage = (pkg, paymentMethodType) => {
  if (
    pkg.type === PACKAGE_TYPE.STUDIO &&
    paymentMethodType &&
    [PAYMENT_METHOD_TYPE.GOOGLE, PAYMENT_METHOD_TYPE.APPLE].includes(
      paymentMethodType,
    )
  )
    return true;

  return pkg?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
};

const checkUsingPrepaidMonthlyPkgOfStudio = (usingPackage) => {
  if (!usingPackage) return false;

  const { type, code, renewalType } = usingPackage;
  return (
    type === PACKAGE_TYPE.STUDIO &&
    code.includes(PACKAGE_DURATION.MONTH) &&
    renewalType !== PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW
  );
};

const getPackageDuration = (code) => {
  if (code.includes(PACKAGE_DURATION.MONTH)) return DURATION.MONTHLY;
  if (code.includes(PACKAGE_DURATION.QUARTER)) return DURATION.QUARTERLY;
  if (code.includes(PACKAGE_DURATION.YEAR)) return DURATION.YEARLY;
  return '';
};

const getPackageDataDuration = (code, isAutoRenew) => {
  if (code.includes(PACKAGE_DURATION.MONTH))
    if (isAutoRenew) return DURATION.MONTHLY;
    else return DURATION.MONTH;
  if (code.includes(PACKAGE_DURATION.QUARTER))
    if (isAutoRenew) return DURATION.QUARTERLY;
    else return DURATION.QUARTER;
  if (code.includes(PACKAGE_DURATION.YEAR))
    if (isAutoRenew) return DURATION.YEARLY;
    else return DURATION.YEAR;
  return '';
};

// TO-DO: handle with other package type
const getPackageTypeFromCode = (code) => {
  if (code.includes(PACKAGE_TYPE.STUDIO)) return PACKAGE_TYPE.STUDIO;
  if (code.includes(PACKAGE_TYPE.DUBBING)) return PACKAGE_TYPE.DUBBING;
  if (code.includes(PACKAGE_TYPE.API)) return PACKAGE_TYPE.API;
  return '';
};

// Every top-up version (-TOPUP-V3, -TOPUP-V3.2, ...) carries the marker; the payment service matches it the same way.
const isTopUpPackageCode = (code = '') => /TOPUP/.test(code);

const getFakeEndTimeDiscountForPackage = ({
  startTimeToCountdown,
  realEndTime,
  timeToResetCountdown,
}) => {
  const startTime = new Date(startTimeToCountdown).getTime();
  const endTime = new Date(realEndTime).getTime();
  const now = new Date().getTime();

  const resetInterval = timeToResetCountdown * 60 * 60 * 1000;

  const timeSinceStart = now - startTime;
  const cycles = Math.floor(timeSinceStart / resetInterval);
  let fakeEndTime = startTime + (cycles + 1) * resetInterval;

  if (fakeEndTime > endTime) {
    fakeEndTime = endTime;
  }

  return new Date(fakeEndTime);
};

const getDurationAndExpireTimeForPkg = (pkgCode) => {
  if (!pkgCode) return '';
  let numDaysToAdd = 0;
  if (pkgCode.includes(PACKAGE_DURATION.DAY)) numDaysToAdd = 1;
  if (pkgCode.includes(PACKAGE_DURATION.WEEK)) numDaysToAdd = 7;
  if (pkgCode.includes(PACKAGE_DURATION.MONTH)) numDaysToAdd = 30;

  return {
    dueDate: moment().add(numDaysToAdd, 'days').format('DD/MM/YYYY'),
    duration: numDaysToAdd,
  };
};

const calculateRemainingResource = (
  productType,
  userInfo,
  isPackageExpired,
) => {
  const {
    remainingCharacters = 0,
    bonusCharacters = 0,
    lockCharacters = 0,
    apiCharacters,
    dubbing,
  } = userInfo || {};
  switch (productType) {
    case PACKAGE_TYPE.STUDIO:
      return isPackageExpired
        ? lockCharacters
        : remainingCharacters + bonusCharacters;

    case PACKAGE_TYPE.API:
      return isPackageExpired
        ? apiCharacters?.lockCharacters
        : apiCharacters?.remainingCharacters ||
            0 + apiCharacters?.bonusCharacters ||
            0;

    case PACKAGE_TYPE.DUBBING:
      return isPackageExpired
        ? dubbing?.lockSeconds
        : dubbing?.remainingSeconds;

    default:
      return 0;
  }
};

const getRedirectUrlPayment = (productType, isFreePackage, code) => {
  switch (productType) {
    case PACKAGE_TYPE.STUDIO:
      return isFreePackage ? route.PAYMENT : `${route.PAYMENT}?package=${code}`;
    case PACKAGE_TYPE.API:
      return isFreePackage
        ? `${VBEE_CONSOLE_URL}/payments`
        : `${VBEE_CONSOLE_URL}/payments?package=${code}`;
    case PACKAGE_TYPE.DUBBING:
      return isFreePackage
        ? route.PAYMENT_DUBBING
        : `${route.PAYMENT_DUBBING}?package=${code}`;

    default:
      return '';
  }
};

const getMergePackageName = (productType) => {
  switch (productType) {
    case PACKAGE_TYPE.STUDIO:
      return MERGE_PRODUCT_TYPE.aivoiceStudio;
    case PACKAGE_TYPE.API:
      return MERGE_PRODUCT_TYPE.aivoiceApi;
    case PACKAGE_TYPE.DUBBING:
      return PRODUCT_TYPE.aivoiceDubbing;
    default:
      return '';
  }
};

const checkHasNextCycle = (packageExpiryDate, period) => {
  // Milliseconds in one day
  const MS_PER_DAY = 86400000;

  const today = new Date();
  const expiryDate = new Date(packageExpiryDate);
  const daysRemaining = Math.max((expiryDate - today) / MS_PER_DAY, 0);
  const remainingCycles = Math.ceil(daysRemaining / period);
  return remainingCycles > 1;
};

const getDaysRemaining = (nextResetAt) => {
  const now = new Date();
  const resetDate = new Date(nextResetAt);
  const timeDifference = resetDate - now;

  const daysRemaining = Math.ceil(timeDifference / MILLISECONDS_IN_DAY);

  return daysRemaining > 0 ? daysRemaining : 0;
};

const getRemainingCycles = (packageExpiryDate, nextResetAt) => {
  // Convert dates to end of day to ensure accurate month difference calculation
  // This prevents edge cases where partial days could affect the month count
  const pkgExpiryDate = moment(packageExpiryDate).endOf('day');
  const nextResetAtDate = moment(nextResetAt).endOf('day');
  return pkgExpiryDate.diff(nextResetAtDate, 'months') || 0;
};

const calculateDaysInCurrentCycle = (startDate) => {
  const currentDate = moment();

  const monthsElapsed = currentDate.diff(startDate, 'months');
  const cycleDurationInMilliseconds =
    moment(startDate).add(monthsElapsed + 1, 'months') -
    moment(startDate).add(monthsElapsed, 'months');

  const daysInCurrentCycle = Math.ceil(
    cycleDurationInMilliseconds / MILLISECONDS_IN_DAY,
  );

  return daysInCurrentCycle > 1 ? daysInCurrentCycle : 1;
};

const calculateTimeToExpiry = (packageExpiryDate) => {
  const remainingDays = moment(packageExpiryDate).diff(moment(), 'days');

  return remainingDays;
};

const getTotalPricePackage = (
  item,
  isDiscountPackage,
  bestVoucher,
  isIpVietNam,
  // TODO: remove this parameter after testing
  roundedPrice = false,
) => {
  let totalPrice = isIpVietNam ? item.price : item.usdPrice;
  if (isDiscountPackage) {
    totalPrice = getPriceFromVoucher({
      isVietNam: isIpVietNam,
      voucher: bestVoucher,
      currPrice: item?.discountConfig?.price,
      currUsdPrice: item?.discountConfig?.usdPrice,
      roundedPrice,
    });
  }

  if (bestVoucher?.value && !isDiscountPackage) {
    totalPrice = getPriceFromVoucher({
      isVietNam: isIpVietNam,
      voucher: bestVoucher,
      currPrice: item.price,
      currUsdPrice: item.usdPrice,
      roundedPrice,
    });
  }

  return totalPrice;
};
const calculateSaleOffMonthlyPrice = (
  item,
  isDiscountPackage,
  bestVoucher,
  isIpVietNam,
  duration,
) => {
  let monthlyPrice = isIpVietNam ? item.monthlyPrice : item.usdMonthlyPrice;

  if (isDiscountPackage) {
    monthlyPrice = getPriceFromVoucher({
      isVietNam: isIpVietNam,
      voucher: bestVoucher,
      currPrice: item?.discountConfig?.monthlyPrice,
      currUsdPrice: item?.discountConfig?.usdMonthlyPrice,
      duration,
      roundedPrice: duration === DURATION.MONTHLY,
    });
  }

  if (bestVoucher?.value && !isDiscountPackage) {
    monthlyPrice = getPriceFromVoucher({
      isVietNam: isIpVietNam,
      voucher: bestVoucher,
      currPrice: item.monthlyPrice || item.price,
      currUsdPrice: item.usdMonthlyPrice || item.usdPrice,
      duration,
      roundedPrice: duration === DURATION.MONTHLY,
    });
  }

  return monthlyPrice;
};

const calculateCharacterPurchase = (credits, topUpPackage) => {
  const { volumeDiscount, creditsPerUnit, vndPricePerUnit, usdPricePerUnit } =
    topUpPackage || {};

  const { discountRatePerStep, creditsPerDiscountStep, maxDiscountRate } =
    volumeDiscount || {};

  const steps = Math.floor(credits / creditsPerDiscountStep);
  const discountRate = Math.min(steps * discountRatePerStep, maxDiscountRate);
  const packagePrice = (credits / creditsPerUnit) * vndPricePerUnit;
  const usdPackagePrice = (credits / creditsPerUnit) * usdPricePerUnit;

  let customPrice = packagePrice * (1 - discountRate);
  let customUsdPrice = usdPackagePrice * (1 - discountRate);

  customPrice = Math.round(customPrice / 1000) * 1000;
  customUsdPrice = Math.round(customUsdPrice * 100) / 100;

  return {
    packagePrice,
    usdPackagePrice,
    customPrice,
    customUsdPrice,
    discountRate,
  };
};

const checkFreePackage = (packageCode) =>
  FREE_PACKAGE_CODES.includes(packageCode);

const roundSaleOffPercentage = (saleOffPercentage) => {
  let saleOffPercentageRounded = Math.round(saleOffPercentage);
  // Nếu làm tròn là 0% mà giá trị thực tế phải trả lớn hơn 0% 1 khoảng nhỏ thì gán bằng 1%
  if (saleOffPercentageRounded === 0 && !!saleOffPercentage) {
    saleOffPercentageRounded = 1;
  }
  // Nếu làm tròn là 100% mà giá trị thực tế phải trả nhỏ hơn 100% 1 khoảng nhỏ thì gán bằng 99%
  if (saleOffPercentageRounded === 100 && saleOffPercentage < 100) {
    saleOffPercentageRounded = 99;
  }
  return saleOffPercentageRounded;
};

/**
 * Generates an ascending array of select options based on price ranges, a step increment, and locale formatting.
 * This is specifically designed for use in an auto complete component.
 *
 * @param {Array<Object>} priceRanges - An array of objects representing price ranges. Each object should include:
 *    - min {number}: The minimum value of the range.
 *    - max {number}: The maximum value of the range.
 *    - unit {string}: The unit of measurement.
 *    - usdPricePerUnit {number}: The price per unit in USD.
 *    - vndPricePerUnit {number}: The price per unit in VND.
 * @param {number} step - The step increment used to generate the range values between the global minimum and maximum.
 *
 * @returns {Array<Object>} An array of option objects. Each option object includes:
 *    - unit {string}: The unit of measurement from the corresponding price range.
 *    - usdPricePerUnit {number}: The price per unit in USD from the corresponding price range.
 *    - vndPricePerUnit {number}: The price per unit in VND from the corresponding price range.
 *    - label {string}: The current value formatted as a locale-specific string.
 *    - value {number}: The numeric value of the option.
 *    - min {number}: The minimum value of the range.
 *    - max {number}: The maximum value of the range.
 */
const generateSelectOptions = ({ prices, step, resource }) => {
  let priceRanges = prices;
  if (
    [
      RESOURCES_TYPE.DEVICES,
      RESOURCES_TYPE.VOICE_CLONINGS,
      RESOURCES_TYPE.CONCURRENT_REQUEST_API,
    ].includes(resource)
  ) {
    priceRanges = priceRanges.map((p) => ({
      ...p,
      min: 0,
      max: p.max - p.min,
    }));
  }
  const minValue = Math.min(...priceRanges.map((p) => p.min));
  const maxValue = Math.max(...priceRanges.map((p) => p.max));
  const count = Math.floor((maxValue - minValue) / step) + 1;

  return Array.from({ length: count }, (_, i) => {
    const value = minValue + i * step;
    const range = priceRanges.find(
      ({ min, max }) => value >= min && value <= max,
    );
    if (range) {
      return {
        unit: range.unit,
        usdPricePerUnit: range.usdPricePerUnit,
        vndPricePerUnit: range.vndPricePerUnit,
        min: range.min,
        max: range.max,
        label: value.toString(),
        value,
      };
    }
    return null;
  }).filter(Boolean);
};

/**
 * Calculates the pricing details for a given resource based on its value and pricing plans.
 * This function takes into account the specific pricing tiers and adjusts the value for non-credit resources.
 *
 * @param {Object} params - The parameters object.
 * @param {number} params.value - The quantity of the resource to calculate the price for.
 * @param {Object} params.pricingPlans - The pricing plan details for the resource, which includes:
 *    - step {number}: The increment step used for pricing.
 *    - prices {Array<Object>}: An array of pricing tiers. Each tier should include:
 *         - min {number}: The minimum value for the tier.
 *         - max {number}: The maximum value for the tier.
 *         - vndPricePerUnit {number}: The price per unit in VND.
 *         - usdPricePerUnit {number}: The price per unit in USD.
 *    - min {number}: The minimum allowed resource value.
 * @param {string} params.resource - The type of resource, used to adjust calculation for credits versus other resources.
 *
 * @returns {Object} An object containing the calculated prices:
 *    - originalVndPrice {number}: The original price in VND calculated with the highest price per unit available.
 *    - originalUsdPrice {number}: The original price in USD calculated with the highest price per unit available.
 *    - vndPrice {number}: The final price in VND based on the applicable pricing tier.
 *    - usdPrice {number}: The final price in USD based on the applicable pricing tier.
 */
const calculateResourcePrice = ({ value, pricingPlans }) => {
  const initialPrices = {
    originalVndPrice: 0,
    originalUsdPrice: 0,
    vndPrice: 0,
    usdPrice: 0,
  };
  const { step, prices } = pricingPlans || {};
  const isValidResourceValue = value && Number.isInteger(value / step);
  if (!isValidResourceValue) return initialPrices;

  const originalPricingTier = prices.reduce((originalOption, currentOption) => {
    const currentUnit = currentOption?.unit || 1;
    const originalUnit = originalOption?.unit || 1;
    const currentPrice = currentOption.vndPricePerUnit / currentUnit;
    const originalPrice = originalOption?.vndPricePerUnit / originalUnit || 0;
    const isMaxPrice = currentPrice > originalPrice;

    return isMaxPrice ? currentOption : originalOption;
  }, {});

  const pricingTier = prices.find((tier) => value >= 0 && value <= tier.max);

  if (!pricingTier || !originalPricingTier) return initialPrices;

  const originalUnit = originalPricingTier?.unit || 1;
  const pricingUnit = pricingTier?.unit || 1;

  return {
    originalVndPrice: roundNumber(
      (value / originalUnit) * originalPricingTier.vndPricePerUnit,
    ),
    originalUsdPrice:
      (value / originalUnit) * originalPricingTier.usdPricePerUnit,
    vndPrice: roundNumber((value / pricingUnit) * pricingTier.vndPricePerUnit),
    usdPrice: (value / pricingUnit) * pricingTier.usdPricePerUnit,
  };
};

/**
 * Calculates the total price for a custom package that includes credits, devices, voice clonings, and API CCR.
 * This function aggregates the individual resource prices to provide a complete package price,
 * and returns prices in VND if isVietNam is true, otherwise in USD.
 *
 * @param {Object} params - The parameters object.
 * @param {number} params.credits - The number of credits included in the package.
 * @param {number} params.devices - The number of devices included in the package.
 * @param {number} params.voiceClonings - The number of voice clonings included in the package.
 * @param {number} params.concurrentRequestApi - The number of API concurrent request streams included in the package.
 * @param {Object} params.customResource - An object containing pricing plans for each resource type:
 *    - credits {Object}: Pricing details for credits.
 *    - devices {Object}: Pricing details for devices.
 *    - voiceClonings {Object}: Pricing details for voice clonings.
 *    - concurrentRequestApi {Object}: Pricing details for API CCR.
 * @param {boolean} params.isVietNam - Flag indicating if the pricing should be returned in VND (true) or USD (false).
 *
 * @returns {Object} An object containing the total price of the custom package:
 *    - price {number}: The aggregated price for the package in the selected currency.
 *    - originalPrice {number}: The aggregated original price (using the highest unit price) for the package in the selected currency.
 */
const getCustomPackagePrice = ({
  credits,
  devices,
  voiceClonings,
  concurrentRequestApi = 0,
  customResource,
  isVietNam,
}) => {
  if (!customResource) {
    return { price: 0, originalPrice: 0 };
  }

  const resources = [
    {
      value: credits,
      pricingPlans: customResource.credits,
    },
    {
      value: devices,
      pricingPlans: customResource.devices,
    },
    {
      value: voiceClonings,
      pricingPlans: customResource.voiceClonings,
    },
    ...(customResource.concurrentRequestApi
      ? [
          {
            value: concurrentRequestApi,
            pricingPlans: customResource.concurrentRequestApi,
          },
        ]
      : []),
  ];

  const initialTotalPrices = {
    vndPrice: 0,
    usdPrice: 0,
    originalVndPrice: 0,
    originalUsdPrice: 0,
  };

  const totalPriceResources = resources.reduce((totalResource, resource) => {
    const calculatePriceResource = calculateResourcePrice(resource);
    return {
      vndPrice: totalResource.vndPrice + calculatePriceResource.vndPrice,
      usdPrice: totalResource.usdPrice + calculatePriceResource.usdPrice,
      originalVndPrice:
        totalResource.originalVndPrice +
        calculatePriceResource.originalVndPrice,
      originalUsdPrice:
        totalResource.originalUsdPrice +
        calculatePriceResource.originalUsdPrice,
    };
  }, initialTotalPrices);

  if (isVietNam) {
    return {
      price: totalPriceResources.vndPrice,
      originalPrice: totalPriceResources.originalVndPrice,
      vndPrice: totalPriceResources.vndPrice,
    };
  }
  return {
    price: totalPriceResources.usdPrice,
    originalPrice: totalPriceResources.originalUsdPrice,
    vndPrice: totalPriceResources.vndPrice,
  };
};

/**
 * Sorts the keys of a custom resource object according to a predefined order.
 * The order is: credits => devices => voiceClonings => concurrentRequestApi
 *
 * @param {Object} customResource - The custom resource object to sort keys from
 * @returns {Array<string>} An array of sorted keys
 */
const sortCustomResourceKeys = (customResource) => {
  const sortOrder = [
    RESOURCES_TYPE.CREDITS,
    RESOURCES_TYPE.DEVICES,
    RESOURCES_TYPE.VOICE_CLONINGS,
    RESOURCES_TYPE.CONCURRENT_REQUEST_API,
  ];

  return Object.keys(customResource).sort((a, b) => {
    const indexA = sortOrder.indexOf(a);
    const indexB = sortOrder.indexOf(b);
    // If both keys are in sortOrder, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only one key is in sortOrder, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    // If neither key is in sortOrder, maintain original order
    return 0;
  });
};

const checkCustomPackage = ({ type, level }) =>
  level === PACKAGE_LEVEL.CUSTOM && type === PACKAGE_TYPE.STUDIO;

const checkOneTimePackage = (version) => {
  if (!version) return true;
  return [
    PACKAGE_STUDIO_VERSION.V1,
    PACKAGE_STUDIO_VERSION.V2_1,
    PACKAGE_STUDIO_VERSION.V2,
  ].includes(version);
};

const checkVipPackage = ({ type, level }) =>
  level === PACKAGE_LEVEL.VIP && type === PACKAGE_TYPE.STUDIO;

const getDefaultValueCustomResource = (customResource) => {
  const { credits } = customResource || {};

  return {
    defaultCredits: credits?.min,
    defaultDevices: 0,
    defaultVoiceClonings: 0,
    concurrentRequestApi: 0,
  };
};

const formatPrice = ({ vndPrice, usdPrice, isVietNam, language }) => {
  if (isVietNam) {
    return `<span style='text-decoration: underline;'>đ</span>${formatNumber(
      vndPrice,
      language,
    )}`;
  }
  return `$${formatNumber(usdPrice, language)}`;
};

const getPlanChange = ({ packageState, usingPackage, user }) => {
  const determinePackageChange = () => {
    if (!usingPackage) return null;
    return getPackageChangeType({
      currPackage: packageState,
      prevPackage: usingPackage,
      prevPackageExpiredDate: user?.packageExpiryDate,
    });
  };
  const packageChangeType = determinePackageChange();
  const isTopUpPackage = packageState?.isTopUp;
  if (isTopUpPackage) return 'topup';
  if (packageChangeType) return packageChangeType;
  return 'same-tier';
};

const checkStudioCustomPackage = (type, level) =>
  type === PACKAGE_TYPE.STUDIO && level === PACKAGE_LEVEL.CUSTOM;

const getStudioFreePackage = (packages) => {
  const freePackage = packages.find(
    (pkg) => pkg.type === PACKAGE_TYPE.STUDIO && checkFreePackage(pkg.code),
  );
  return freePackage;
};

const formatNumberWithSuffix = (num, language = 'en') => {
  const suffixes = {
    en: {
      K: 'K',
      M: 'M',
      B: 'B',
    },
    vi: {
      K: 'K',
      M: ' triệu',
      B: ' tỷ',
    },
  };

  const langSuffixes = suffixes[language] || suffixes.en;
  const format = (value, suffix) =>
    `${value.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}${suffix}`;

  if (num >= 1e9) return format(num / 1e9, langSuffixes.B);
  if (num >= 1e6) return format(num / 1e6, langSuffixes.M);
  if (num >= 1e3) return format(num / 1e3, langSuffixes.K);
  return num?.toString();
};

const calculateMinutesFromWords = (wordCount) => wordCount / WORDS_PER_MINUTE;

const calculateWordsFromMinutes = (minutes) => minutes * WORDS_PER_MINUTE;

const roundToOneDecimal = (value) => Math.round(value * 10) / 10;

const formatDuration = (minutes) => {
  if (minutes >= 60) {
    const hours = roundToOneDecimal(minutes / 60);
    return { value: hours, unit: 'hour' };
  }
  return { value: roundToOneDecimal(minutes), unit: 'minute' };
};

const calculateA4Pages = (wordCount) =>
  roundToOneDecimal(wordCount / WORD_PER_PAGE);

const getParamsExpiringSoon = (usingPackage, packages = []) => {
  const currentPackage =
    packages.find(
      (pkg) =>
        pkg.level === usingPackage?.level &&
        pkg.type === usingPackage?.type &&
        pkg.expiresIn === usingPackage?.expiresIn,
    ) || usingPackage;
  const isCustomPackage = checkCustomPackage({
    type: currentPackage?.type,
    level: currentPackage?.level,
  });

  const baseParamsCustomPackage = {
    package: currentPackage?.code,
    credits: currentPackage?.customResource?.credits?.min,
    devices: 0,
    voiceClonings: 0,
  };
  const defaultCreditsPercycle = currentPackage?.cycle?.creditsPerCycle;
  const creditOptions = currentPackage?.cycle?.creditOptions;
  const creditsPerCycle =
    creditOptions?.find(
      (option) => option?.creditsPerCycle === defaultCreditsPercycle,
    )?.creditsPerCycle || creditOptions?.[0]?.creditsPerCycle;

  const baseParamsCyclePackage = {
    package: currentPackage?.code,
    creditsPerCycle,
  };
  const baseParams = isCustomPackage
    ? baseParamsCustomPackage
    : baseParamsCyclePackage;

  return new URLSearchParams(baseParams).toString();
};

const hasPackageExpiringSoon = (packageExpiryDate, days) => {
  const UI_NOTIFY_NEAR_EXPIRY_SECONDS = 24 * 60 * 60;
  return (
    moment(packageExpiryDate).diff(moment(), 'seconds') > 0 &&
    moment(packageExpiryDate).diff(moment(), 'seconds') <
      days * UI_NOTIFY_NEAR_EXPIRY_SECONDS
  );
};

const checkPackageExpired = (date) => date && moment(date).isBefore(moment());

// A package keeps its `features` list after `packageExpiryDate` passes, so a gate
// that reads the list alone grants a paid feature to a lapsed account. A free
// account carries no expiry date, which `checkPackageExpired` treats as falsy, so
// it stays on the `features` check alone.
const hasActiveFeature = ({ user, feature }) => {
  if (!feature) return false;
  if (checkPackageExpired(user?.packageExpiryDate)) return false;

  return Boolean(user?.studioUsageOptions?.features?.includes(feature));
};

const checkLowCredits = ({ totalCredits, packageCode }) => {
  const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);
  if (isFreePackage) return totalCredits < LOW_CREDITS.FREE_PACKAGE;

  return totalCredits < LOW_CREDITS.PAID_PACKAGE;
};

const getPathPaymentWhenNotifyLowCredits = (pkg, packages) => {
  const isCustomPackage = checkCustomPackage({
    type: pkg?.type,
    level: pkg?.level,
  });
  const minCredits = pkg?.customResource?.credits?.min;
  const minDevices = 0;
  const minVoiceClonings = 0;
  if (isCustomPackage)
    return `${route.PAYMENT}?package=${pkg?.code}&credits=${minCredits}&devices=${minDevices}&voiceClonings=${minVoiceClonings}`;

  const topUpPackageCode = pkg?.topUpPackageCode;
  if (topUpPackageCode) {
    const topUpPackage = packages?.find(
      (p) => p.type === pkg?.type && p.level === pkg?.level && p.isTopUp,
    );
    const redirectUrl = `${route.PAYMENT}?package=${topUpPackage?.code}`;
    return redirectUrl;
  }

  return route.PAYMENT;
};

const checkShouldNotifyLowCredits = ({
  beforeCredits,
  afterCredits,
  packageCode,
}) => {
  const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);
  if (isFreePackage)
    return (
      beforeCredits >= LOW_CREDITS.FREE_PACKAGE &&
      afterCredits < LOW_CREDITS.FREE_PACKAGE
    );

  return (
    beforeCredits >= LOW_CREDITS.PAID_PACKAGE &&
    afterCredits < LOW_CREDITS.PAID_PACKAGE
  );
};

const getPackageTopUp = (pkg, packages) => {
  const topUpPackage = packages?.find(
    (p) => p.type === pkg?.type && p.level === pkg?.level && p.isTopUp,
  );
  return topUpPackage;
};

const roundUpToOneDecimal = (num) => {
  if (Number.isInteger(num) && num > 0) return num;
  return Math.round(num * 10) / 10;
};

// Cheapest package (by level) that carries `feature`, or null.
const getMinPackageForFeature = ({ packages, feature }) => {
  if (!feature) return null;
  const eligible = (packages || []).filter((p) =>
    p?.features?.includes(feature),
  );
  if (!eligible.length) return null;
  return [...eligible].sort(
    (a, b) =>
      PACKAGE_LEVEL_ORDER.indexOf(a.level) -
      PACKAGE_LEVEL_ORDER.indexOf(b.level),
  )[0];
};

const getMinPackageNameForFeature = ({ packages, feature, language }) => {
  const pkg = getMinPackageForFeature({ packages, feature });
  return pkg?.name?.[language] || pkg?.name?.en || pkg?.level || '';
};

export {
  formatPrice,
  getDefaultValueCustomResource,
  roundSaleOffPercentage,
  calculateSaleOffMonthlyPrice,
  getTotalPricePackage,
  calculateDaysInCurrentCycle,
  getDaysRemaining,
  getRemainingCycles,
  getPackageChangeType,
  getPackageNameV3,
  filterPackagesV3,
  filterPackages,
  getPackageName,
  getPackagesName,
  checkHasDiscountPackage,
  getNewPackageCurrentPrice,
  getOldPackageCurrentPrice,
  getPackageCurrentPrice,
  getDiscountPackage,
  getMostPopularPackage,
  checkAutoRenewalPackage,
  checkPackageDiscount,
  checkUsingPrepaidMonthlyPkgOfStudio,
  getPackageDuration,
  getPackageTypeFromCode,
  getFakeEndTimeDiscountForPackage,
  getDurationAndExpireTimeForPkg,
  calculateRemainingResource,
  getRedirectUrlPayment,
  getMergePackageName,
  checkHasNextCycle,
  calculateCharacterPurchase,
  checkFreePackage,
  generateSelectOptions,
  calculateResourcePrice,
  getCustomPackagePrice,
  checkCustomPackage,
  checkOneTimePackage,
  calculateTimeToExpiry,
  getPackageDataDuration,
  getPlanChange,
  checkStudioCustomPackage,
  checkVipPackage,
  getStudioFreePackage,
  formatNumberWithSuffix,
  calculateMinutesFromWords,
  calculateWordsFromMinutes,
  roundToOneDecimal,
  formatDuration,
  calculateA4Pages,
  getParamsExpiringSoon,
  hasPackageExpiringSoon,
  checkLowCredits,
  checkPackageExpired,
  getPathPaymentWhenNotifyLowCredits,
  checkShouldNotifyLowCredits,
  sortCustomResourceKeys,
  getPackageTopUp,
  roundUpToOneDecimal,
  getMinPackageForFeature,
  getMinPackageNameForFeature,
  hasActiveFeature,
  isTopUpPackageCode,
};
